<script lang="ts" setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import RouteStopIcon from "@/components/icons/RouteStopIcon.vue";
import { useValidation } from "@/composables/useValidation";
import { getEntityKey } from "@/utils/sameEntity";
import { getStopShortInfo } from "@/utils/stopInfoTitle";
import type { PodDetailsGroup } from "@/types/trip";
import type { RouteStopDto } from "@/api/trips/dto/trip.dto";

const { t } = useI18n();
const { requiredValidator } = useValidation();

const signedBy = defineModel<string>("signedBy", { default: "" });

const props = withDefaults(
  defineProps<{
    group: PodDetailsGroup;
    readonly?: boolean;
    stop: RouteStopDto;
  }>(),
  {
    readonly: false,
  },
);

const pickUpInfo = computed<string>(() => {
  const pickUp = props.group.pickUp;

  if (!pickUp) {
    return "";
  }

  return getStopShortInfo(pickUp);
});

const isReload = computed(() => {
  return props.stop.isReload;
});
</script>

<template>
  <div class="bg-grey-lighten-4 border rounded-lg pa-4 d-flex align-start col-gap-4">
    <div class="flex-1">
      <div class="d-flex flex-wrap align-center col-gap-3 row-gap-1 mb-3">
        <span v-if="group.bol" class="font-size-14 font-weight-500 text-grey-navy" data-qa="pod-bol-number">
          {{ t("trip.bol-pod-number", { number: group.bol.number }) }}
        </span>

        <div v-if="group.pickUp" class="d-flex align-center col-gap-1" data-qa="pod-pick-up">
          <route-stop-icon :type="group.pickUp.type" size="20" />
          <span class="font-size-14 font-weight-500 text-grey-darken-1">{{ pickUpInfo }}</span>
        </div>
      </div>

      <div class="d-flex flex-column row-gap-2">
        <div
          v-for="freight in group.freights"
          :key="getEntityKey(freight)"
          class="font-size-14 text-grey-navy"
          data-qa="pod-freight"
        >
          {{ freight.name }}
        </div>
      </div>
    </div>

    <v-text-field
      v-if="group.bol && !isReload"
      v-model="signedBy"
      :disabled="readonly"
      :label="t('general.signed-by')"
      :rules="[requiredValidator]"
      autocomplete="off"
      class="required bg-white pod-card__signed-by"
      color="primary"
      data-qa="signed-by-field"
      density="compact"
      hide-details="auto"
      variant="outlined"
    />
  </div>
</template>

<style lang="scss" scoped>
.pod-card__signed-by {
  flex: 0 0 350px;
  max-width: 350px;
}
</style>
